import { EntityType, useAppSelector } from '@frontend/common';
import { InsaneFilter } from '@frontend/components/list';
import { Application, Feature } from '@frontend/functionality/types';
import { CommonMessage, useLocale } from '@frontend/lang';
import { initList, listStore } from '@frontend/repository';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { usePlatformModalHost } from '../../../../modal/platform-modal-host/platform-modal-host.context';
import { CategoryRow } from './category-row.component';
import { useDirectoryView } from './directory-view.controller';

interface DirectoryViewProps {
    navigate: ReturnType<typeof import('react-router-dom').useNavigate>;
    location: ReturnType<typeof import('react-router-dom').useLocation>;
    dispatch: ThunkDispatch<any, any, Action>;
    listId: string;
}

export const DirectoryView: React.FC<DirectoryViewProps> = ({ dispatch, listId }) => {
    const { isLoading, toggleCategory, getFilteredCategories, expandedCategories } = useDirectoryView();
    const { locale } = useLocale();
    const { openPlatformDetail } = usePlatformModalHost();
    const store = useAppSelector(useSelector, listStore);
    const list = store[listId];

    useEffect(() => {
        if (!list) {
            dispatch(initList({ listId, defaultListValues: {} }));
        }
    }, [dispatch, list, listId]);

    const searchQueries = useMemo(() => {
        if (list?.activeFilters?.search && list.activeFilters.search.length > 0) {
            return list.activeFilters.search;
        }
        return [];
    }, [list?.activeFilters?.search]);

    const filteredCategories = getFilteredCategories(locale, searchQueries);

    return (
        <div className='bg-background-surface border-border-default w-full rounded-xl border'>
            {list && (
                <InsaneFilter
                    dispatch={dispatch}
                    listId={listId}
                    searchKeys={['search']}
                    displayName={new Map([['search', CommonMessage.OBJECTS.CATEGORY.CATEGORY_NAME]])}
                />
            )}

            <div className='border-border-default border-b'>
                <div className='text-foreground-default px-4 py-3 font-medium'>{CommonMessage.OBJECTS.CATEGORY.CATEGORY_NAME}</div>
            </div>

            {isLoading ? (
                <div className='flex items-center justify-center p-8'>
                    <p>{CommonMessage.FORMS.LOADING_PLACEHOLDER}...</p>
                </div>
            ) : filteredCategories.length === 0 && searchQueries.length === 0 ? (
                <div className='flex items-center justify-center p-8'>
                    <p>{CommonMessage.ERRORS.ENTITY_NOT_FOUND(CommonMessage.OBJECTS.CATEGORY.PLURAL)}</p>
                </div>
            ) : filteredCategories.length === 0 && searchQueries.length > 0 ? (
                <div className='flex items-center justify-center p-8'>
                    <p>{CommonMessage.ERRORS.ENTITY_NO_MATCH(CommonMessage.OBJECTS.CATEGORY.PLURAL, searchQueries.join(', '))}</p>
                </div>
            ) : (
                <div className='flex flex-col'>
                    {filteredCategories.map((categoryNode, index) => {
                        const rootPath = [categoryNode.category.id];
                        return (
                            <CategoryRow
                                key={rootPath.join('/')}
                                node={categoryNode}
                                depth={0}
                                path={rootPath}
                                expandedCategories={expandedCategories}
                                onToggle={toggleCategory}
                                onCategoryClick={(category) => openPlatformDetail({ entityType: EntityType.PRODUCT_CATEGORY, id: category.id })}
                                onProductClick={(product) =>
                                    openPlatformDetail({
                                        entityType: EntityType.PRODUCT,
                                        id: product.id,
                                        context: { application: Application.CATALOGUE, feature: Feature.PRODUCTS }
                                    })
                                }
                                locale={locale}
                                isLast={index === filteredCategories.length - 1}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
};
